import { memo, type FC } from 'react'

import {
  BarColor,
  StackedBar,
  barColors,
  disabledBarColor,
} from '@factorialco/deprecated-design-system'

import i18n from 'modules/core/lib/i18n'
import type { AnalyticResult } from 'modules/core/store/models/Analytic'

import gendersCollection from 'modules/settings/store/collections/genders'

export interface Props {
  results: AnalyticResult<number>[]
  error?: string
}

const getAnalyticId = (id: AnalyticResult<number>['id']): number => {
  switch (typeof id) {
    case 'number':
      return id
    case 'string':
      return parseInt(id)
    default:
      return 0
  }
}

const isDefaultGender = (id: number): boolean => {
  const name = getGenderName(id)
  return name === 'female' || name === 'male'
}

const getGenderName = (id: number) => {
  const gender = gendersCollection.get(id)
  return gender?.name?.toLowerCase()
}

const GenderStackedBar: FC<Props> = ({ results, error }) => {
  const [genders, otherGenders] = results.reduce<
    [AnalyticResult<number>[], AnalyticResult<number>]
  >(
    (acc, x) => {
      const [prevGenders, prevOtherGenres] = acc
      const id = getAnalyticId(x.id)

      if (x.value < 0.1 && !isDefaultGender(id))
        return [prevGenders, { ...prevOtherGenres, value: prevOtherGenres.value + x.value }]

      return [[...prevGenders, x], prevOtherGenres]
    },
    [[], { id: null, label: i18n.t('common.others'), value: 0 }]
  )

  const bars = [...genders, otherGenders].map((x, i) => {
    const { id, label, value } = x
    const percentage = Math.floor(value * 100)
    const genderId = getAnalyticId(x.id)
    const gender = getGenderName(genderId)
    const [femaleColor, maleColor, undefinedColor, ...otherColors] = barColors
    let color: BarColor

    switch (gender) {
      case 'female':
        color = femaleColor as BarColor
        break
      case 'male':
        color = maleColor as BarColor
        break
      case undefined:
        color = undefinedColor as BarColor
        break
      default:
        color = otherColors[i] as BarColor
    }

    const labelColor = color ? color[0]?.split(/[0-9]/)[0] : undefinedColor
    const dataTestIdLabel = `${label}-${labelColor}`

    return (
      <StackedBar.Bar
        key={id}
        value={percentage}
        label={`${label} ${percentage}%`}
        color={color}
        data-testid={dataTestIdLabel || ''}
      />
    )
  })

  return (
    <StackedBar>
      {error ? (
        <StackedBar.Bar value={100} color={disabledBarColor} label={error} data-testid="error" />
      ) : (
        bars
      )}
    </StackedBar>
  )
}

export default memo(GenderStackedBar)
